<script>
import ButtonAll from '../UI/Button/ButtonAll.vue'
import WaitingText from '../UI/Text/WaitingText.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import { useDocumentTypeUnicity } from '../../Composables/useDocumentTypeUnicity.js'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import { toBase64 } from '../../Utils/file.ts'
import uniqueId from 'lodash/uniqueId'

export default {
  components: { ModalConfirmation, WaitingText, ButtonAll },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    update: { type: String, required: true },
    document: Object,
    uuid: { type: String, required: false },
    context: { type: String, required: false },
    contextId: { type: String, required: false },
  },
  emits: ['document-uploading', 'document-uploaded', 'document-uploading-error'],
  data() {
    return {
      uploading: false,
      uploadError: false,
      documentToSend: {},
    }
  },
  computed: {
    confirmationModalId() {
      return uniqueId(`document_upload_${this.update}_${this.document.id}`)
    },
  },
  methods: {
    async modifyDocument(fileList) {
      if (fileList.length <= 0) {
        return
      }

      /** @var File **/
      const file = fileList[0]
      if (this.conf.fileMaxSize > 0 && file.size > this.conf.fileMaxSize) {
        let fileMaxSize = this.conf.fileMaxSize
        if (fileMaxSize < 1024) {
          fileMaxSize = fileMaxSize + ' o'
        } else {
          fileMaxSize = fileMaxSize / 1024
          if (fileMaxSize < 1024) {
            fileMaxSize = fileMaxSize + ' Ko'
          } else {
            fileMaxSize = fileMaxSize / 1024 + ' Mo'
          }
        }
        alert(this.$root.$t('document.errorFileMaxSize', { fileMaxSize: fileMaxSize }))
        return
      }
      let fileExtensions = this.conf.fileExtensionAuthorized.toLowerCase().split(',')
      let fileExtension = ''
      let index = file.name.lastIndexOf('.')
      if (index > 0) {
        fileExtension = file.name.substring(index + 1).toLowerCase()
      }
      if (fileExtensions.indexOf(fileExtension) < 0) {
        fileExtension = ''
        for (let i = 0; i < fileExtensions.length; i++) {
          if (i > 0) {
            fileExtension += ', '
          }
          fileExtension += "'." + fileExtensions[i] + "'"
        }
        alert(this.$root.$t('document.errorFileExtension', { fileExtension: fileExtension }))
        return
      }

      this.uploading = true

      // Envoie le fichier sur le serveur
      this.setUploading(this.document)
      this.documentToSend = {}
      if (this.update === 'modify') {
        this.documentToSend.docAssocId = this.document.id
      } else if (this.update === 'new') {
        this.documentToSend.mandatoryId = this.document.id
      } else if (this.update === 'add') {
        this.documentToSend.typeId = this.document.id
        this.documentToSend.context = 'AppBundle\\Entity\\' + this.context
        this.documentToSend.contextId = this.contextId
      }
      this.documentToSend.uniqid = this.uuid
      this.documentToSend.imageFile = {
        filename: file.name,
        file: '',
      }

      this.documentToSend.imageFile.file = await toBase64(file)
      if (!this.requireConfirmation) {
        await this.uploadDocument()
      } else {
        this.$refs.confirm.showModal()
      }
    },

    setUploading(document) {
      this.$emit('document-uploading', document)
    },

    setUploadOk(document, documentUploaded) {
      this.$emit('document-uploaded', documentUploaded, document)
    },

    setUploadError(document, reason) {
      this.uploading = !this.uploading
      this.$emit('document-uploading-error', { document: document, error: reason })
    },

    changeClassButton(id, enter) {
      if (enter) {
        $('#button-' + id).addClass('btn-outline')
      } else {
        $('#button-' + id).removeClass('btn-outline')
      }
    },
    async uploadDocument() {
      let url = 'documents'
      if (!!this.uuid) {
        url = 'free/' + url
      }

      try {
        this.uploading = true
        await this.postJSON(url, this.documentToSend, { displayError: true, noWaitingModal: true })
        this.toast(this.$t('document.uploadingOk', { fileName: this.documentToSend.imageFile.filename }))
        this.uploading = false
        this.uploadError = false

        if (this.$parent?.getDocuments) {
          this.$parent?.getDocuments()
        }

        this.$emit('document-uploaded', this.document, this.documentToSend)
      } catch (error) {
        this.uploadError = true
        this.setUploadError(this.document, error)
      } finally {
        this.documentToSend = {}
      }
    },
    onConfirmation() {
      this.$refs.confirm.closeModal()
      this.uploadDocument()
    },
    onCancel() {
      this.uploading = false
    },
  },
  setup(props) {
    let documentType
    if (props.update === 'new') {
      documentType = props.document.documentType
    } else {
      documentType = props.document.document.documentType
    }

    const { requireConfirmation, confirmationTitle, confirmationMessage } = useDocumentTypeUnicity(
      'upload',
      documentType,
      props.update === 'new',
    )

    return {
      requireConfirmation,
      confirmationTitle,
      confirmationMessage,
    }
  },
}
</script>
<template>
  <div v-bind="$attrs" class="dropbox d-flex align-items-center">
    <template v-if="!uploading">
      <input
        type="file"
        :id="document.id"
        @change="modifyDocument($event.target.files)"
        @mouseover="changeClassButton(document.id, true)"
        @mouseout="changeClassButton(document.id, false)"
        class="dropbox-input-file"
        :title="$t('document.clickForUpload')"
        e2e-input="documentFileUpload"
      />
      <ButtonAll
        v-if="update !== 'modify'"
        :id="'button-' + document.id"
        type="drop_add"
        size="small"
        margin="no"
        label="documentEntity.buttonAdd"
        name="documentAdd"
        height="full"
        @document-uploading-error="setUploadError"
      />
      <ButtonAll
        v-if="update === 'modify'"
        :id="'button-' + document.id"
        type="drop_modify"
        size="small"
        margin="no"
        label="documentEntity.buttonModify"
        name="documentUpdate"
        height="full"
      />
    </template>
    <WaitingText v-if="uploading">{{ $t('document.uploading') }}</WaitingText>
    <span v-if="uploadError" class="badge-danger badge-lg badge-pill fs-14" :title="document.uploadError">
      <i class="fa fa-warning"></i>&nbsp;&nbsp;{{ $t('document.uploadingError') }}
    </span>
    <template v-if="requireConfirmation">
      <Teleport to="body">
        <ModalConfirmation
          :id="confirmationModalId"
          ref="confirm"
          :title="confirmationMessage"
          :message="confirmationTitle"
          @confirm="onConfirmation"
          @cancel="onCancel"
        />
      </Teleport>
    </template>
  </div>
</template>
